<template>

  <SidebarComponent :collapsible="isMobile ? 'offcanvas' : 'icon'" variant="floating">
    <SidebarHeader class="pt-2 px-0 gap-0">
      <div
        class="flex"
        :class="{
          'flex-row items-center justify-between px-2': isOpened,
          'flex-col gap-2': !isOpened,
        }"
      >
        <div 
        class="cursor-pointer p-2 flex items-center justify-center"
        :class="{
          'max-w-12': !isOpened
        }"
        @click="handleLogoClick" 
        >
          <LogoSquare v-if="!isOpened" variant="black" />

          <LogoFull v-else class="h-5 w-auto" />
        </div>

        <div class=" flex items-center justify-center">
          <SidebarTrigger
          >
            <ArrowRightToLine
              class="size-4 "
              :class="{
                'rotate-180': isOpened,
                'opacity-0': triggerIsHidden,
                'opacity-100': !triggerIsHidden,
              }"
          />
        </SidebarTrigger>
        </div>
      </div>
    </SidebarHeader>

      <SidebarContent :class="{'mt-[32px] pr-5': isOpened}" class="">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem v-for="item in links" :key="item.id">
                <SidebarMenuButton variant="outline"  as-child :is-active="page.startsWith(item.id)" >
                    <inertia-link :href="item.href">
                      <component :is="item.icon" />

                        <span
                          class="whitespace-nowrap transition-opacity ease-in"
                          :class="isOpened ? 'opacity-100 duration-500 delay-200' : 'opacity-0 duration-100 delay-0'"
                        >
                          {{ item.name }}
                        </span>

                      <SidebarMenuBadge
                        class="flex! transition-opacity ease-in"
                        :class="isOpened ? 'opacity-100 duration-500 delay-200' : 'opacity-0 duration-100 delay-0'"
                      >{{ item.count }}</SidebarMenuBadge>

                    </inertia-link>

                    <SidebarMenuSub v-if="open && !isMobile && page.startsWith(item.id) && item.children">
                      <SidebarMenuSubItem v-for="child in item.children" :key="child.id" >
                        <SidebarMenuSubButton as-child size="sm" :is-active="page === child.id">
                          <inertia-link :href="child.href">
                            <span
                              class="whitespace-nowrap transition-opacity ease-in"
                              :class="isOpened ? 'opacity-100 duration-500 delay-200' : 'opacity-0 duration-100 delay-0'"
                            >
                              {{ child.name }}
                            </span>
                          </inertia-link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                </SidebarMenuButton>
              </SidebarMenuItem>
          </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

    <SidebarFooter class="p-2 mt-1">
      <SidebarTeamSwitch />
    </SidebarFooter>

  </SidebarComponent>
</template>

<script lang="ts" setup>


import {
  SidebarComponent,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import sidebar from "@/data/sidebar";
import sidebarNew from "@/data/sidebar-new";
import pluginSidebar from "@/data/pluginSidebar-new";
import { ArrowRightToLine } from "lucide-vue-next";
import { ILink } from "@/types/Atomic/ISidebarData";
import { ITopolPageProps } from "@/types/TopolPageProps/TopolPageProps";



const { isMobile, openMobile, open, setOpenMobile } = useSidebar();

let removeNavigateListener: (() => void) | null = null;

onMounted(() => {
    removeNavigateListener = router.on('navigate', () => {
        if (isMobile.value) {
            setOpenMobile(false);
        }
    });
});

onUnmounted(() => {
    removeNavigateListener?.();
});
const { links: kioskLinks } = sidebar();
const { links: appLinks } = sidebarNew();
const { links: pluginLinks } = pluginSidebar();

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const page = computed(() => usePage<ITopolPageProps>().url);

const isKiosk = computed(() => {
    return page.value.startsWith('/kiosk');
});

// Computed (not a ref+onMounted snapshot) so the badge counts/labels stay reactive
// when underlying data (e.g. membersCount) updates after the page has mounted.
const links = computed<ILink[]>(() => {
    if (isKiosk.value) return kioskLinks.value;
    const appType = globalProps.value.user.app_type;
    if (appType === 'plugin') return pluginLinks.value;
    if (appType === 'pro' || appType === 'both' || appType === null) return appLinks.value;
    return [];
});

const isOpened = computed(() => {
  return (open.value && !isMobile.value) || (openMobile.value && isMobile.value);
});

const handleLogoClick = () => {
  if (isMobile.value) {
    openMobile.value = false;
  }

  router.visit('/');
};

const triggerIsHidden = ref(false)

watch(
  () => isOpened.value,
  async () => {
    // Hide
    triggerIsHidden.value = true

    // Wait 400ms
    await new Promise(resolve => setTimeout(resolve, 400))

    // Show again (now rotated already)
    triggerIsHidden.value = false
  }
)

</script>

<style scoped>
.fade-enter-active {
  transition: opacity 1s;
}

.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
